// Reads the IAB GPP signal from the CMP (OneTrust). Used for the video ad tag, because Google's
// video SDK does not read the CMP the way GPT and apstag do.
// GPP 1.1 CMP API: https://github.com/InteractiveAdvertisingBureau/Global-Privacy-Platform/blob/main/Core/CMP%20API%20Specification.md

export const GPP_TIMEOUT_MS = 3000;

// -1 and 0 mean "not applicable" per the spec, so they must not reach demand partners.
const toGppSignal = pingData => {
    if (!pingData?.gppString) return undefined;
    const applicableSections = (pingData.applicableSections || []).filter(section => section > 0);
    return applicableSections.length ? { gppString: pingData.gppString, applicableSections } : undefined;
};

/**
 * Resolves the current GPP signal once the CMP reports it ready. addEventListener calls back
 * straight away with the current pingData, so an already-ready CMP needs no later event.
 * Never rejects: resolves undefined when there is no CMP, the CMP errors or throws, no section
 * applies, or GPP_TIMEOUT_MS passes.
 *
 * @returns {Promise<{gppString: string, applicableSections: number[]}|undefined>}
 */
export function getGppData() {
    return new Promise(resolve => {
        const gpp = window.__gpp;
        if (typeof gpp !== 'function') {
            resolve(undefined);
            return;
        }
        let settled = false;
        let listenerId;
        let listenerRemoved = false;

        const removeListener = () => {
            if (listenerId === undefined || listenerRemoved) return;
            listenerRemoved = true;
            try {
                gpp('removeEventListener', () => {}, listenerId);
            } catch (e) {
                // The signal is already resolved; a listener left behind is harmless.
            }
        };

        // Resolve before removing the listener, so a CMP that throws on remove cannot leave this
        // promise pending and hold the player.
        const finish = pingData => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            resolve(toGppSignal(pingData));
            removeListener();
        };

        const timer = setTimeout(() => finish(undefined), GPP_TIMEOUT_MS);
        try {
            gpp('addEventListener', (event, success) => {
                if (!success || !event) return;
                listenerId = event.listenerId;
                // A first callback after the timeout still registered a listener, so drop it.
                if (settled) {
                    removeListener();
                    return;
                }
                const { pingData } = event;
                if (!pingData) return;
                if (pingData.cmpStatus === 'error') {
                    finish(undefined);
                } else if (pingData.signalStatus === 'ready') {
                    finish(pingData);
                }
            });
        } catch (e) {
            finish(undefined);
        }
    });
}
